'use client';

import { useLanguage } from '@/context/LanguageContext';
import { Check, ArrowLeft, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Reveal from '@/components/Reveal';
import type { ServicePage } from '@/i18n/service-data';
import type { Locale } from '@/i18n/config';

const colorMap: Record<string, { border: string; bg: string; text: string; glow: string; ctaBorder: string; ctaBg: string; ctaHoverBg: string; ctaShadow: string; ctaHoverShadow: string }> = {
  amber: { border: 'border-amber-400/30', bg: 'bg-amber-400/10', text: 'text-amber-400', glow: 'hover:shadow-[0_0_30px_rgba(251,191,36,0.15)]', ctaBorder: 'border-amber-400', ctaBg: 'bg-amber-400/10', ctaHoverBg: 'hover:bg-amber-400/20', ctaShadow: 'shadow-[0_4px_0_0_#B8860B]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#B8860B]' },
  brand: { border: 'border-electric/30', bg: 'bg-electric/10', text: 'text-electric', glow: 'hover:shadow-[0_0_30px_rgba(79,142,255,0.15)]', ctaBorder: 'border-electric', ctaBg: 'bg-electric/10', ctaHoverBg: 'hover:bg-electric/20', ctaShadow: 'shadow-[0_4px_0_0_#1E8FA6]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#1E8FA6]' },
  rose: { border: 'border-rose-400/30', bg: 'bg-rose-400/10', text: 'text-rose-400', glow: 'hover:shadow-[0_0_30px_rgba(251,113,133,0.15)]', ctaBorder: 'border-rose-400', ctaBg: 'bg-rose-400/10', ctaHoverBg: 'hover:bg-rose-400/20', ctaShadow: 'shadow-[0_4px_0_0_#9F1239]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#9F1239]' },
  emerald: { border: 'border-emerald-400/30', bg: 'bg-emerald-400/10', text: 'text-emerald-400', glow: 'hover:shadow-[0_0_30px_rgba(52,211,153,0.15)]', ctaBorder: 'border-emerald-400', ctaBg: 'bg-emerald-400/10', ctaHoverBg: 'hover:bg-emerald-400/20', ctaShadow: 'shadow-[0_4px_0_0_#065F46]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#065F46]' },
  purple: { border: 'border-purple-400/30', bg: 'bg-purple-400/10', text: 'text-purple-400', glow: 'hover:shadow-[0_0_30px_rgba(192,132,252,0.15)]', ctaBorder: 'border-purple-400', ctaBg: 'bg-purple-400/10', ctaHoverBg: 'hover:bg-purple-400/20', ctaShadow: 'shadow-[0_4px_0_0_#6B21A8]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#6B21A8]' },
  blue: { border: 'border-blue-400/30', bg: 'bg-blue-400/10', text: 'text-blue-400', glow: 'hover:shadow-[0_0_30px_rgba(96,165,250,0.15)]', ctaBorder: 'border-blue-400', ctaBg: 'bg-blue-400/10', ctaHoverBg: 'hover:bg-blue-400/20', ctaShadow: 'shadow-[0_4px_0_0_#1E40AF]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#1E40AF]' },
  orange: { border: 'border-orange-400/30', bg: 'bg-orange-400/10', text: 'text-orange-400', glow: 'hover:shadow-[0_0_30px_rgba(251,146,60,0.15)]', ctaBorder: 'border-orange-400', ctaBg: 'bg-orange-400/10', ctaHoverBg: 'hover:bg-orange-400/20', ctaShadow: 'shadow-[0_4px_0_0_#9A3412]', ctaHoverShadow: 'hover:shadow-[0_2px_0_0_#9A3412]' },
};

export default function ServicePageClient({
  service,
  locale,
}: {
  service: ServicePage;
  locale: Locale;
}) {
  const { lang, t } = useLanguage();
  const content = service[lang];
  const colors = colorMap[service.color] ?? colorMap.brand;

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-20 bg-dark-950 min-h-screen">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <Reveal>
            <a
              href={`/${lang}#services`}
              className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-electric transition-colors duration-300 mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('Retour à l\u2019accueil', 'Back to home')}
            </a>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              {content.headline}
            </h1>
            <p className="text-lg text-white/50 leading-relaxed max-w-2xl">
              {content.intro}
            </p>
          </Reveal>
        </section>

        {/* Features */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="grid gap-6 md:grid-cols-2">
            {content.features.map((feature, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className={`rounded-2xl border ${colors.border} bg-white/[0.03] backdrop-blur-sm p-6 h-full transition-all duration-500 ${colors.glow}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <Check className={`w-5 h-5 mt-0.5 shrink-0 ${colors.text}`} />
                    <h3 className="text-lg font-bold text-white">{feature.title}</h3>
                  </div>
                  <p className="text-white/40 text-sm leading-relaxed pl-8">
                    {feature.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <a
              href={`/${lang}#contact`}
              className={`inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white ${colors.ctaBg} border ${colors.ctaBorder} rounded-xl ${colors.ctaHoverBg} transition-all duration-300 ${colors.ctaShadow} ${colors.ctaHoverShadow} hover:translate-y-[2px]`}
            >
              {content.cta}
              <ArrowRight className="w-5 h-5" />
            </a>
          </Reveal>
        </section>
      </main>
      <Footer />
    </>
  );
}
